import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Plus,
  Trash2,
  Code,
  AlertTriangle,
  Check,
  ChevronDown,
  ImageOff,
  ShoppingBag,
  Settings2,
  Tag,
  User,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/lib/api/stores';
import localeData from '@/data/locale.json';
import { LANGUAGES } from '@/data/languages';
import { PriceLineItemsSelector, PriceLineItem } from '@/components/form/price-line-items-selector';
import { cn } from '@/lib/utils';

/** A checkout step the generated link can point at. */
export interface DirectLinkTarget {
  id: string;
  label: string;
  /** Step page path, shown under the name — funnels often carry several
   *  checkouts whose names differ only by a suffix. */
  path?: string;
  /** Page preview, same capture the canvas step boxes render. */
  thumbnailUrl?: string;
  /** Draft/staging URL of the step page. */
  checkoutUrl?: string;
  /** Published URL of the step page, when the funnel is live. */
  productionCheckoutUrl?: string;
}

interface DirectLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  /** Checkout steps to choose between. The picker only shows when there are 2+. */
  targets: DirectLinkTarget[];
  /** Which target to open on. Falls back to the first one. */
  initialTargetId?: string;
  funnelId?: string;
}

interface QueryParam {
  key: string;
  value: string;
}

const AUTO_LOCALE = 'auto';
type LanguageCode = keyof (typeof localeData)['primary-dialects'];

export function DirectLinkDialog({ isOpen, onClose, storeId, targets, initialTargetId, funnelId }: DirectLinkDialogProps) {
  const { t } = useTranslation('storefront');
  const { data: store } = useStore(storeId, !!storeId);

  const [environment, setEnvironment] = useState<'staging' | 'production'>('production');
  const [outputFormat, setOutputFormat] = useState<'link' | 'button'>('link');
  const [copied, setCopied] = useState(false);
  const [targetId, setTargetId] = useState<string | undefined>(initialTargetId ?? targets[0]?.id);
  // Opening from a node's "Get Link" shortcut already names the target, so the
  // grid starts collapsed there and expanded when the header action opened it
  // with nothing chosen yet.
  const [isPickerExpanded, setIsPickerExpanded] = useState(!initialTargetId);

  // Reopening for a different step must retarget, otherwise the dialog keeps
  // generating for whichever step it was opened on the first time. Keyed on ids
  // rather than the array itself: `targets` is rebuilt on every parent render,
  // so depending on its identity would snap the picker back to the initial step
  // the moment anything upstream re-rendered.
  const firstTargetId = targets[0]?.id;
  useEffect(() => {
    if (isOpen) {
      setEnvironment('production');
      setCopied(false);
      setTargetId(initialTargetId ?? firstTargetId);
      setIsPickerExpanded(!initialTargetId);
    }
  }, [isOpen, initialTargetId, firstTargetId]);

  const target = targets.find((s) => s.id === targetId) ?? targets[0];
  const checkoutUrl = target?.checkoutUrl;
  const productionCheckoutUrl = target?.productionCheckoutUrl;

  const [lineItems, setLineItems] = useState<PriceLineItem[]>([]);
  const [currency, setCurrency] = useState(store?.baseCurrency || 'EUR');
  const [locale, setLocale] = useState<LanguageCode | typeof AUTO_LOCALE>(AUTO_LOCALE);
  const [returnUrl, setReturnUrl] = useState('');
  const [buttonText, setButtonText] = useState('Buy Now');
  const [buttonColor, setButtonColor] = useState('#10B981');
  const [queryParams, setQueryParams] = useState<QueryParam[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [includeCheckoutToken, setIncludeCheckoutToken] = useState(true);
  const [checkoutTokenParamName, setCheckoutTokenParamName] = useState('checkoutToken');

  const [showCustomer, setShowCustomer] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTracking, setShowTracking] = useState(false);

  useEffect(() => {
    if (store && !store.presentmentCurrencies.includes(currency)) {
      setCurrency(store.baseCurrency);
    }
  }, [store, currency]);

  const handleAddQueryParam = () => setQueryParams((prev) => [...prev, { key: '', value: '' }]);
  const handleRemoveQueryParam = (index: number) => setQueryParams((prev) => prev.filter((_, i) => i !== index));
  const handleUpdateQueryParam = (index: number, updates: Partial<QueryParam>) => {
    setQueryParams((prev) => prev.map((param, i) => (i !== index ? param : { ...param, ...updates })));
  };
  const handleAddCommonParam = (key: string, value: string) => {
    const existingIndex = queryParams.findIndex((p) => p.key === key);
    if (existingIndex >= 0) {
      handleUpdateQueryParam(existingIndex, { value });
    } else {
      setQueryParams((prev) => [...prev, { key, value }]);
    }
  };

  const generateDynamicLink = useCallback(
    (items: PriceLineItem[], storeId: string, currency: string, locale: string) => {
      const validItems = items.filter((item) => item.variantId && item.priceId && item.quantity > 0);
      if (validItems.length === 0) return '';

      const cartItems = validItems.map((item) => ({
        variantId: item.variantId,
        quantity: item.quantity,
        ...(item.priceId && { priceId: item.priceId }),
      }));

      try {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://api.tagada.io';
        const apiUrl = `${baseUrl}/api/public/v1/checkout/init`;

        let selectedCheckoutUrl: string | undefined;
        if (environment === 'production') {
          selectedCheckoutUrl = productionCheckoutUrl;
        } else {
          selectedCheckoutUrl = checkoutUrl;
        }

        const params = new URLSearchParams({
          currency,
          items: JSON.stringify(cartItems),
          storeId,
          // funnelId is required so the checkout plugin initialises a
          // funnel session (POST /api/v1/funnel/initialize) and links it to
          // the checkout session. Without it, checkoutSession.funnelSessionId
          // stays null and post-payment resolution falls back to the V1
          // /post/:orderId redirect instead of following the V2 step graph.
          ...(funnelId && { funnelId }),
          ...(returnUrl && { returnUrl }),
          ...(selectedCheckoutUrl && { checkoutUrl: selectedCheckoutUrl }),
          ...(customerId && { customerId }),
          ...(customerEmail && { customerEmail }),
          ...(customerFirstName && { customerFirstName }),
          ...(customerLastName && { customerLastName }),
          ...(customerPhone && { customerPhone }),
          ...(includeCheckoutToken && { includeCheckoutToken: checkoutTokenParamName }),
          ...(environment === 'staging' && { draft: 'true' }),
        });

        if (locale !== AUTO_LOCALE) {
          const fullLocale = localeData['primary-dialects'][locale as LanguageCode];
          params.append('locale', fullLocale);
        }

        queryParams.forEach(({ key, value }) => {
          if (key && value) params.append(key, value);
        });

        return `${apiUrl}?${params.toString()}`;
      } catch (error) {
        console.error('Error generating dynamic link:', error);
        return '';
      }
    },
    [returnUrl, checkoutUrl, productionCheckoutUrl, environment, queryParams, customerId, customerEmail, customerFirstName, customerLastName, customerPhone, includeCheckoutToken, checkoutTokenParamName, funnelId],
  );

  /**
   * The selected step has no page URL in the chosen environment — for
   * production that means it exists only in the draft and the funnel has not
   * been promoted since it was added.
   *
   * This has to block generation, not just warn: `checkoutUrl` is appended
   * conditionally, so an undefined value produced a perfectly valid-looking
   * link that silently dropped the target and opened the store's DEFAULT
   * checkout instead of the selected step (TAG-2138).
   */
  const targetUrlForEnvironment = environment === 'production' ? productionCheckoutUrl : checkoutUrl;
  const isTargetUnavailable = !!target && !targetUrlForEnvironment;

  const dynamicUrl = useMemo(() => {
    if (isTargetUnavailable) return '';
    if (lineItems.some((item) => item.variantId && item.priceId && item.quantity > 0)) {
      return generateDynamicLink(lineItems, storeId, currency, locale);
    }
    return '';
  }, [isTargetUnavailable, lineItems, storeId, currency, locale, generateDynamicLink]);

  const generateButtonHtml = (url: string) => {
    return `<a href="${url}" style="display:inline-block;padding:12px 24px;background-color:${buttonColor};color:white;text-decoration:none;border-radius:6px;font-family:system-ui,-apple-system,sans-serif;font-weight:500">${buttonText}</a>`;
  };

  const handleCopyUrl = async () => {
    if (!dynamicUrl) {
      toast.error(t('directLink.addProductFirst'));
      return;
    }
    await navigator.clipboard.writeText(dynamicUrl);
    setCopied(true);
    toast.success(t('directLink.linkCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyHtml = async () => {
    if (!dynamicUrl) {
      toast.error(t('directLink.addProductFirst'));
      return;
    }
    await navigator.clipboard.writeText(generateButtonHtml(dynamicUrl));
    setCopied(true);
    toast.success(t('directLink.htmlCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenInNewTab = () => {
    if (!dynamicUrl) {
      toast.error(t('directLink.addProductFirst'));
      return;
    }
    window.open(dynamicUrl, '_blank');
  };

  const hasProducts = lineItems.some((item) => item.variantId && item.priceId && item.quantity > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-[640px] flex-col gap-0 overflow-hidden p-0">

        {/* ─── Header ───────────────────────────────────────── */}
        <div className="shrink-0 border-b border-slate-200 px-5 pb-3 pt-5 dark:border-slate-700">
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-base font-semibold">
              {t('directLink.title')}
            </DialogTitle>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {t('directLink.description')}
            </p>
          </DialogHeader>

          {/* Environment toggle — compact pill */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800">
              <button
                onClick={() => setEnvironment('production')}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-all',
                  environment === 'production'
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                )}
              >
                {t('directLink.production')}
              </button>
              <button
                onClick={() => setEnvironment('staging')}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-all',
                  environment === 'staging'
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                )}
              >
                {t('directLink.test')}
              </button>
            </div>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {environment === 'staging' ? t('directLink.draftDataForTesting') : t('directLink.liveCheckout')}
            </span>
          </div>
        </div>

        {/* ─── Scrollable body ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Checkout target — only a choice worth showing when the funnel has
              more than one checkout step. With a single one there is nothing to
              pick, so the row would be dead weight above every link.

              Cards rather than a text dropdown: funnels routinely carry several
              checkouts whose names differ only by a suffix ("… - Vibriance",
              "V2 … - Vibriance"), which a name-only list cannot tell apart. */}
          {targets.length > 1 && (
            <div className="border-b border-slate-200 bg-slate-50/60 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/40">
              <Label className="text-xs text-slate-600 dark:text-slate-400">
                {t('directLink.targetCheckout')}
              </Label>
              {isPickerExpanded && (
                <p className="mt-0.5 text-[10px] text-slate-400">{t('directLink.targetCheckoutHint')}</p>
              )}

              {/* Collapsed: the grid has done its job once a step is chosen, and
                  leaving it open pushes the products and the generated link down
                  the dialog for the rest of the session. */}
              {!isPickerExpanded && target && (
                <button
                  type="button"
                  onClick={() => setIsPickerExpanded(true)}
                  className="mt-2 flex w-full items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-1.5 text-left transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600"
                >
                  <div className="h-9 w-14 shrink-0 overflow-hidden rounded bg-slate-100 dark:bg-slate-900">
                    {target.thumbnailUrl ? (
                      <img src={target.thumbnailUrl} alt="" className="h-full w-full object-cover object-top" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-300 dark:text-slate-600">
                        <ImageOff className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-[11px] font-medium text-slate-700 dark:text-slate-300">{target.label}</p>
                      {!target.productionCheckoutUrl && (
                        <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          {t('directLink.notPublished')}
                        </span>
                      )}
                    </div>
                    {target.path && (
                      <p className="truncate font-mono text-[9px] text-slate-400">{target.path}</p>
                    )}
                  </div>
                  <span className="shrink-0 pr-1 text-[11px] font-medium text-primary">
                    {t('directLink.changeCheckout')}
                  </span>
                </button>
              )}

              {isPickerExpanded && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {targets.map((s) => {
                  const isSelected = s.id === target?.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setTargetId(s.id);
                        setIsPickerExpanded(false);
                      }}
                      aria-pressed={isSelected}
                      title={s.label}
                      className={cn(
                        'group relative overflow-hidden rounded-lg border bg-white text-left transition-all dark:bg-slate-800',
                        isSelected
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
                      )}
                    >
                      {/* Fixed-ratio frame so a missing capture leaves the grid
                          aligned instead of collapsing that card's height. */}
                      <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100 dark:bg-slate-900">
                        {s.thumbnailUrl ? (
                          <img
                            src={s.thumbnailUrl}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover object-top"
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-300 dark:text-slate-600">
                            <ImageOff className="h-4 w-4" />
                            <span className="text-[9px] font-medium">{t('directLink.noPreview')}</span>
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white shadow">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        {/* Draft-only steps cannot carry a production link.
                            Flagged here so the state is visible while choosing,
                            not after copying a link that silently retargets. */}
                        {!s.productionCheckoutUrl && (
                          <span className="absolute left-1.5 top-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                            {t('directLink.notPublished')}
                          </span>
                        )}
                      </div>

                      <div className="border-t border-slate-100 px-2 py-1.5 dark:border-slate-700">
                        <p className={cn(
                          'truncate text-[11px] font-medium',
                          isSelected ? 'text-primary' : 'text-slate-700 dark:text-slate-300',
                        )}>
                          {s.label}
                        </p>
                        {s.path && (
                          <p className="truncate font-mono text-[9px] text-slate-400">{s.path}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* STEP 1 — Products */}
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                1
              </div>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {t('directLink.selectProducts')}
              </span>
            </div>
            <PriceLineItemsSelector
              storeId={storeId}
              lineItems={lineItems}
              onLineItemsChange={setLineItems}
              label=""
              required
              // A checkout link builds a session to transact — per-order-SKU
              // merchants keep every product inactive by design (TAG-1595).
              includeInactive
            />
          </div>

          {/* STEP 2 — Settings */}
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div className="mb-3 flex items-center gap-2">
              <div className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold',
                hasProducts
                  ? 'bg-primary text-white'
                  : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
              )}>
                2
              </div>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {t('directLink.settings')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600 dark:text-slate-400">{t('directLink.currency')}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="mt-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {store?.presentmentCurrencies.map((code) => (
                      <SelectItem key={code} value={code}>{code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 dark:text-slate-400">{t('directLink.language')}</Label>
                <Select
                  value={locale}
                  onValueChange={(value) => setLocale(value as LanguageCode | typeof AUTO_LOCALE)}
                >
                  <SelectTrigger className="mt-1 h-8 text-sm">
                    <SelectValue placeholder={t('directLink.autoDetect')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    <SelectItem value={AUTO_LOCALE}>{t('directLink.autoDetect')}</SelectItem>
                    {Object.entries(localeData['primary-dialects']).map(([code]) => {
                      const language = LANGUAGES[code as keyof typeof LANGUAGES];
                      const label = language ? `${language.nativeName} (${language.name})` : code.toUpperCase();
                      return <SelectItem key={code} value={code}>{label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Expandable sections */}
            <div className="mt-3 space-y-0 rounded-lg border border-slate-200 dark:border-slate-700">

              {/* Customer prefill */}
              <ToggleSection
                icon={<User className="h-3.5 w-3.5" />}
                label={t('directLink.prefillCustomerInfo')}
                open={showCustomer}
                onToggle={() => setShowCustomer(!showCustomer)}
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] text-slate-500">{t('directLink.email')}</Label>
                    <Input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="mt-0.5 h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">{t('directLink.customerId')}</Label>
                    <Input
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      placeholder="cus_abc123"
                      className="mt-0.5 h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">{t('directLink.firstName')}</Label>
                    <Input
                      value={customerFirstName}
                      onChange={(e) => setCustomerFirstName(e.target.value)}
                      placeholder={t('directLink.firstNamePlaceholder')}
                      className="mt-0.5 h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">{t('directLink.lastName')}</Label>
                    <Input
                      value={customerLastName}
                      onChange={(e) => setCustomerLastName(e.target.value)}
                      placeholder={t('directLink.lastNamePlaceholder')}
                      className="mt-0.5 h-8 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[11px] text-slate-500">{t('directLink.phone')}</Label>
                    <Input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="+1 555-123-4567"
                      className="mt-0.5 h-8 text-sm"
                    />
                  </div>
                </div>
              </ToggleSection>

              {/* UTM / Tracking params */}
              <ToggleSection
                icon={<Tag className="h-3.5 w-3.5" />}
                label={t('directLink.trackingParameters')}
                badge={queryParams.length > 0 ? `${queryParams.length}` : undefined}
                open={showTracking}
                onToggle={() => setShowTracking(!showTracking)}
                borderTop
              >
                {queryParams.length > 0 && (
                  <div className="space-y-1.5">
                    {queryParams.map((param, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <Input
                          placeholder={t('directLink.key')}
                          value={param.key}
                          onChange={(e) => handleUpdateQueryParam(index, { key: e.target.value })}
                          className="h-8 flex-1 text-sm"
                        />
                        <span className="text-slate-300">=</span>
                        <Input
                          placeholder={t('directLink.value')}
                          value={param.value}
                          onChange={(e) => handleUpdateQueryParam(index, { value: e.target.value })}
                          className="h-8 flex-1 text-sm"
                        />
                        <button
                          onClick={() => handleRemoveQueryParam(index)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/30"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {['utm_source', 'utm_medium', 'utm_campaign', 'cid'].map((key) => {
                    const exists = queryParams.some((p) => p.key === key);
                    return (
                      <button
                        key={key}
                        onClick={() => !exists && handleAddCommonParam(key, '')}
                        disabled={exists}
                        className={cn(
                          'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                          exists
                            ? 'border-slate-200 bg-slate-50 text-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-600'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-primary dark:hover:text-primary'
                        )}
                      >
                        {key}
                      </button>
                    );
                  })}
                  <button
                    onClick={handleAddQueryParam}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-300"
                  >
                    <Plus className="h-3 w-3" />
                    {t('directLink.custom')}
                  </button>
                </div>
              </ToggleSection>

              {/* Advanced */}
              <ToggleSection
                icon={<Settings2 className="h-3.5 w-3.5" />}
                label={t('directLink.advanced')}
                open={showAdvanced}
                onToggle={() => setShowAdvanced(!showAdvanced)}
                borderTop
              >
                <div className="space-y-3">
                  <div>
                    <Label className="text-[11px] text-slate-500">{t('directLink.returnUrl')}</Label>
                    <Input
                      type="url"
                      value={returnUrl}
                      onChange={(e) => setReturnUrl(e.target.value)}
                      placeholder="https://yoursite.com/thank-you"
                      className="mt-0.5 h-8 text-sm"
                    />
                    <p className="mt-0.5 text-[10px] text-slate-400">{t('directLink.returnUrlHint')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="includeCheckoutToken"
                      checked={includeCheckoutToken}
                      onCheckedChange={(checked) => setIncludeCheckoutToken(checked as boolean)}
                    />
                    <Label htmlFor="includeCheckoutToken" className="cursor-pointer text-xs font-normal text-slate-600 dark:text-slate-400">
                      {t('directLink.includeCheckoutToken')}
                    </Label>
                  </div>
                  {includeCheckoutToken && (
                    <div className="ml-6">
                      <Label className="text-[11px] text-slate-500">{t('directLink.parameterName')}</Label>
                      <Input
                        value={checkoutTokenParamName}
                        onChange={(e) => setCheckoutTokenParamName(e.target.value)}
                        placeholder="checkoutToken"
                        className="mt-0.5 h-8 text-sm"
                      />
                    </div>
                  )}
                </div>
              </ToggleSection>
            </div>
          </div>
        </div>

        {/* ─── Sticky footer — output & actions ─────────────── */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">

          {/* Checked before the products guard: no amount of product picking
              makes a link valid while its target page does not exist in the
              selected environment. */}
          {isTargetUnavailable ? (
            <div className="flex items-start gap-2.5 px-5 py-3.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
                  {environment === 'production'
                    ? t('directLink.targetNotPublished', { step: target?.label ?? '' })
                    : t('directLink.targetNoDraftUrl', { step: target?.label ?? '' })}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  {environment === 'production'
                    ? t('directLink.targetNotPublishedFix')
                    : t('directLink.targetNoDraftUrlFix')}
                </p>
              </div>
            </div>
          ) : !hasProducts ? (
            <div className="px-5 py-4 text-center">
              <ShoppingBag className="mx-auto h-5 w-5 text-slate-300 dark:text-slate-600" />
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                {t('directLink.addProductsToGenerate')}
              </p>
            </div>
          ) : (
            <div className="px-5 py-3">
              {/* Output format tabs */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800">
                  <button
                    onClick={() => setOutputFormat('link')}
                    className={cn(
                      'flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-all',
                      outputFormat === 'link'
                        ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                    )}
                  >
                    <LinkIcon className="h-3 w-3" />
                    {t('directLink.link')}
                  </button>
                  <button
                    onClick={() => setOutputFormat('button')}
                    className={cn(
                      'flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-all',
                      outputFormat === 'button'
                        ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                    )}
                  >
                    <Code className="h-3 w-3" />
                    {t('directLink.htmlButton')}
                  </button>
                </div>
                <button
                  onClick={handleOpenInNewTab}
                  className="flex items-center gap-1 text-[11px] text-slate-500 transition-colors hover:text-primary dark:text-slate-400"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('directLink.testInBrowser')}
                </button>
              </div>

              {outputFormat === 'link' ? (
                <>
                  {/* URL preview */}
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="truncate font-mono text-[11px] text-slate-600 dark:text-slate-400" title={dynamicUrl}>
                      {dynamicUrl}
                    </p>
                  </div>
                  <Button
                    onClick={handleCopyUrl}
                    className="mt-2 w-full gap-2"
                    size="sm"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? t('directLink.copied') : t('directLink.copyLink')}
                  </Button>
                </>
              ) : (
                <>
                  {/* Button customization */}
                  <div className="mb-2 flex items-center gap-2">
                    <Input
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder={t('directLink.buttonTextPlaceholder')}
                      className="h-8 flex-1 text-sm"
                    />
                    <Input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                      className="h-8 w-10 shrink-0 cursor-pointer p-0.5"
                    />
                  </div>
                  {/* Preview */}
                  <div className="mb-2 flex items-center justify-center rounded-md border border-slate-200 bg-white py-3 dark:border-slate-700 dark:bg-slate-800">
                    <div dangerouslySetInnerHTML={{ __html: generateButtonHtml(dynamicUrl || '#') }} />
                  </div>
                  <Button
                    onClick={handleCopyHtml}
                    className="w-full gap-2"
                    size="sm"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? t('directLink.copied') : t('directLink.copyHtml')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleSection({
  icon,
  label,
  badge,
  open,
  onToggle,
  borderTop,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: string;
  open: boolean;
  onToggle: () => void;
  borderTop?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(borderTop && 'border-t border-slate-200 dark:border-slate-700')}>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
        <span className="flex-1 text-xs font-medium text-slate-700 dark:text-slate-300">{label}</span>
        {badge && (
          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
            {badge}
          </span>
        )}
        <ChevronDown className={cn('h-3.5 w-3.5 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}
